import { useEffect, useRef, useState } from "react";
import { DEFAULT_PAGE_SIZE } from "../../../configs/apiConfig";
import { FilterType } from "../../../configs/filterTypes";
import { ColumnAttribute } from "../../../configs/productOrder";
import { SortType } from "../../../configs/sortTypes";
import Spinner from "../LoadingPage";
import PaginationBar from "../PaginationBar";
import SearchFilterBar from "../SearchFilterBar";
import { api } from "../../hooks/api";
import { ContentWrapper, ExpandableContent } from "../../styled/TableStyles";
import CustomTable from "../CustomTable";

interface TableViewProps {
    url: string;
    defaultPageSize?: number;
    processItem?: Function;
    sortTypes?: SortType[];
    filterTypes?: FilterType[];
    rowElement: any;
    tableColumns: ColumnAttribute[];
    textString?: string;
    refresh?: boolean;
    selectable?: boolean;
    onSelectionChange?: (selectedItems: any[]) => void;
    defaultFilterParams?: Record<string, string>;
}

const TableView = ({
    url,
    defaultPageSize,
    processItem,
    sortTypes,
    filterTypes,
    rowElement,
    tableColumns,
    textString,
    refresh,
    selectable,
    onSelectionChange,
    defaultFilterParams,
}: TableViewProps) => {
    const lastRefreshRef = useRef<boolean | undefined>(refresh);

    // Page/size state — used directly for client-side pagination,
    // and passed to PaginationBar for server-side pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize || DEFAULT_PAGE_SIZE);

    const initialParams = {
        page_size: defaultPageSize || DEFAULT_PAGE_SIZE,
        page: 1,
        ...(defaultFilterParams || {}),
    };

    // Disable auto-fetch when filterTypes provided — wait for user to apply filters first
    const [{ data, isLoading }, fetchItems] = api.get(url, initialParams, Boolean(filterTypes || defaultFilterParams));

    useEffect(() => {
        if (refresh === undefined) return;
        if (lastRefreshRef.current === refresh) return;
        if (!isLoading) {
            lastRefreshRef.current = refresh;
            fetchItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh, isLoading]);

    // Auto-detect pagination mode from response shape:
    //   plain array  → client-side pagination (API returns all, FE slices)
    //   {results, count} → server-side pagination (API handles pages)
    const isClientPaginated = Array.isArray(data);
    const rawItems = isClientPaginated ? data : data?.results || [];
    const processedItems: any[] = processItem ? rawItems.map(processItem) : rawItems;
    const totalItems = isClientPaginated ? processedItems.length : data?.count || 0;

    // Client-side: slice the full list for current page
    const visibleItems = isClientPaginated
        ? processedItems.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : processedItems;

    // Client-side pagination doesn't re-fetch — page/size changes only update state
    const noop = () => {};
    const paginationFetchItems = isClientPaginated ? noop : fetchItems;

    const clearAllFilters = () => {
        setCurrentPage(1);
        fetchItems({ page_size: DEFAULT_PAGE_SIZE, page: 1, ...(defaultFilterParams || {}) }, true);
    };

    return (
        <ContentWrapper>
            <ExpandableContent data-testid="expandable-content">
                <SearchFilterBar
                    textString={textString}
                    sortTypes={sortTypes}
                    fetchItems={fetchItems}
                    filterTypes={filterTypes}
                    setCurrentPage={setCurrentPage}
                    onRefresh={() => fetchItems()}
                    onClearFilters={clearAllFilters}
                    defaultFilterParams={defaultFilterParams}
                />
                {isLoading ? (
                    <Spinner />
                ) : (
                    <>
                        <CustomTable
                            itemList={visibleItems}
                            RowElement={rowElement}
                            TableColumns={tableColumns}
                            extraRowProps={{ fetchItems }}
                            selectable={selectable}
                            onSelectionChange={onSelectionChange}
                        />
                        {visibleItems.length <= 15 && (
                            <PaginationBar
                                totalItems={totalItems}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                fetchItems={paginationFetchItems}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                            />
                        )}
                    </>
                )}
            </ExpandableContent>
            {visibleItems.length > 15 && (
                <PaginationBar
                    totalItems={totalItems}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    fetchItems={paginationFetchItems}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                />
            )}
        </ContentWrapper>
    );
};

export default TableView;
